import { SanityImage } from '@/components/content/SanityImage';
import { urlForSanityImage } from '@/lib/sanity';
import { Icon } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import { track } from '@/lib/analytics';
import { cart, cartItemFromProduct, useCart } from '../stores/cart';
import { cartUi } from '../stores/cartUi';
import { memberPriceCents, useMembership } from '@/lib/membership';
import type {
  MarketplaceProduct,
  MarketplaceRecommendation,
} from '@/types/api';

/**
 * ProductCard — mirrors the `non-list` variant of system-web's
 * `components/product/product-card.js`:
 *   - Tall image fills the card (~4:5)
 *   - Floating price pill (top-left, dark + blur)
 *   - Bottom overlay (dark + blur) with title + first tag and subtitle
 *   - "Add to cart" inverse-button below the card with the brand Arrow
 *     icon on the right (justify-between per the arrow-button pattern)
 */
export function ProductCard({
  product,
  onOpenDetail,
  onSwitchFormat,
  recommendation,
}: {
  product: MarketplaceProduct;
  onOpenDetail?: (product: MarketplaceProduct) => void;
  /** Set when the patient already owns another FORMAT of this chemical
   *  (e.g. NAD injection while viewing NAD nasal) — the CTA becomes a
   *  switch flow instead of a duplicate purchase. */
  onSwitchFormat?: (product: MarketplaceProduct) => void;
  recommendation?: MarketplaceRecommendation;
}) {
  const inCart = useCart((s) => s.items.some((i) => i.id === product.id));
  const price = product.price_cents ?? 0;
  const { percent: memberPercent } = useMembership();
  const memberPrice = memberPriceCents(price, memberPercent);
  const cadence =
    product.purchase_mode === 'one_time_course' ? 'one-time course' : '/ mo.';
  const tagLabel = product.tags?.[0];

  const handleAdd = () => {
    if (product.dose_offers?.length && onOpenDetail) {
      onOpenDetail(product);
      return;
    }
    if (onSwitchFormat) {
      track('marketplace_switch_format_opened', {
        product_id: product.id,
        slug: product.slug,
        recommended: Boolean(recommendation),
        recommendation_rank: recommendation?.rank ?? null,
      });
      onSwitchFormat(product);
      return;
    }
    if (inCart) return;
    track('marketplace_product_clicked', {
      product_id: product.id,
      slug: product.slug,
      price_cents: price,
      recommended: Boolean(recommendation),
      recommendation_rank: recommendation?.rank ?? null,
    });
    cart.add(cartItemFromProduct(product));
    // Straight into the cart drawer — no second tap on the sticky bar.
    cartUi.open();
  };

  // Build the Sanity URL ourselves with width-only (no height, no
  // fit:'crop') so the asset arrives at its natural aspect ratio.
  // SanityImage's default builder hardcodes fit:'crop' which is what
  // was causing the visible crop in the app vs system-web — system-web
  // also requests width-only (see components/product/product-card.js).
  const heroUrl =
    urlForSanityImage(product.hero_image, { width: 1200 }) ??
    product.hero_image_url;

  const handleOpenDetail = () => {
    if (!onOpenDetail) return;
    track('marketplace_product_detail_opened', {
      product_id: product.id,
      slug: product.slug,
      recommended: Boolean(recommendation),
      recommendation_rank: recommendation?.rank ?? null,
    });
    onOpenDetail(product);
  };

  return (
    <div className="flex flex-col gap-0">
      {/* Whole card (minus the Add button) opens the benefits modal. */}
      <div
        role={onOpenDetail ? 'button' : undefined}
        tabIndex={onOpenDetail ? 0 : undefined}
        onClick={handleOpenDetail}
        onKeyDown={(e) => {
          if (onOpenDetail && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleOpenDetail();
          }
        }}
        className={cn(
          'relative w-full pb-[128%] sm:pb-[110%] overflow-hidden bg-sunken',
          onOpenDetail && 'cursor-pointer',
        )}
      >
        <SanityImage
          url={heroUrl}
          alt={product.title}
          width={600}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Price pill — top-left, dark translucent w/ backdrop blur. */}
        {price > 0 && memberPrice === price && (
          <div className="absolute left-8 top-8 sm:left-10 sm:top-10 bg-black/50 backdrop-blur-md px-8 sm:px-10 py-5 sm:py-6 text-white text-12 sm:text-14 flex items-center gap-2">
            <span>${(price / 100).toFixed(0)}</span>
            <span className="text-10">{cadence}</span>
          </div>
        )}
        {/* Member price: 10% off, the regular price struck through. */}
        {price > 0 && memberPrice !== price && (
          <div className="absolute left-8 top-8 sm:left-10 sm:top-10 bg-black/50 backdrop-blur-md px-8 sm:px-10 py-5 sm:py-6 text-white text-12 sm:text-14 flex flex-col gap-2">
            <span className="font-mono text-9 sm:text-10 uppercase text-[#CFE3D6]">
              Member price
            </span>
            <span className="flex items-center gap-4">
              <span>
                ${(memberPrice / 100).toFixed(memberPrice % 100 ? 2 : 0)}
              </span>
              <span className="text-10">{cadence}</span>
              <span className="text-10 text-white/60 line-through">
                ${(price / 100).toFixed(0)}
              </span>
            </span>
          </div>
        )}

        {/* Personalized + format-switch indicators share a stack so they
            never overlap when a recommended item is another format. */}
        {(recommendation || onSwitchFormat) && (
          <div className="absolute right-8 top-8 sm:right-10 sm:top-10 flex flex-col items-end gap-6">
            {recommendation && (
              <div className="bg-[#f5f5f5] px-6 sm:px-10 py-5 sm:py-6 text-black font-mono text-9 sm:text-10 uppercase shadow-sm">
                <span className="sm:hidden">Recommended</span>
                <span className="hidden sm:inline">Recommended for you</span>
              </div>
            )}
            {onSwitchFormat && (
              <div className="bg-[var(--accent)] px-6 sm:px-10 py-5 sm:py-6 text-white font-mono text-9 sm:text-10 uppercase">
                <span className="sm:hidden">Switch</span>
                <span className="hidden sm:inline">Switch available</span>
              </div>
            )}
          </div>
        )}

        {/* Bottom info overlay — sits on top of the image. */}
        <div className="absolute inset-x-0 bottom-0 p-8 sm:p-10">
          <div className="bg-black/50 backdrop-blur-md text-white px-10 sm:px-15 py-8 sm:py-10 flex flex-col gap-5">
            <div className="flex items-end justify-between gap-10">
              <h3 className="font-mono text-11 sm:text-14 uppercase leading-110">
                {product.title}
              </h3>
              {tagLabel && (
                <div className="hidden sm:block type-label-xs text-white/60 pb-3">
                  {tagLabel.toUpperCase()}
                </div>
              )}
            </div>
            {(product.treatment_support || product.subtitle) && (
              <p className="hidden sm:block type-body-sm text-white/80 leading-snug">
                {product.treatment_support || product.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleAdd}
        disabled={!product.dose_offers?.length && !onSwitchFormat && inCart}
        className={cn(
          'w-full h-44 sm:h-50 flex items-center justify-between px-10 sm:px-15 text-12 sm:text-14 font-body',
          'bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        <span>
          {product.dose_offers?.length ? 'Choose dosage' : onSwitchFormat ? 'Switch format' : inCart ? 'In cart' : 'Add'}
        </span>
        <span aria-hidden className="inline-flex w-8 h-10">
          <Icon name="Arrow" viewBox="0 0 10 12" />
        </span>
      </button>
    </div>
  );
}
